import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CaretLeft, CaretRight, X, Plus, Download, PencilSimple, Trash, Gear } from "@phosphor-icons/react";
import { fmtLocalDate } from "@/lib/utils";
import { humanizeAxiosError } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

// Leave-type labels shown on the schedule when a day is marked as a specific leave.
const LEAVE_LABELS = {
  annual: "Yıllık İzin",
  excuse: "Mazeret",
  hourly: "Saatlik",
  sick: "Hastalık",
  maternity: "Doğum",
  report: "Rapor",
  unpaid: "Ücretsiz",
  parental: "Analık",
  paternity: "Babalık",
  marriage: "Evlilik",
  bereavement: "Ölüm",
};

function mondayOf(d) {
  const x = new Date(d);
  const day = x.getDay() || 7;
  if (day !== 1) x.setDate(x.getDate() - (day - 1));
  x.setHours(0, 0, 0, 0);
  return x;
}
function isoDate(d) { return fmtLocalDate(d); }

const DEFAULT_TYPE_FORM = { code: "", label: "", start_time: "09:00", end_time: "17:00", color: "#C05A42", department: null };

export default function Shifts() {
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [data, setData] = useState({ shifts: [], leaves: [] });
  const [personnel, setPersonnel] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm, setTypeForm] = useState(DEFAULT_TYPE_FORM);
  const [exportWeeks, setExportWeeks] = useState(1);
  const [deptFilter, setDeptFilter] = useState("__all__");

  const canEdit = ["admin", "hr", "gm", "manager"].includes(user?.role);
  const canManageTypes = ["admin", "hr", "gm", "manager"].includes(user?.role);
  const canExport = ["admin", "hr", "gm", "manager"].includes(user?.role);

  const load = useCallback(() => {
    api.get(`/shifts?week_start=${isoDate(weekStart)}`).then((r) => setData(r.data));
    api.get("/personnel").then((r) => setPersonnel(r.data));
    api.get("/shift-types").then((r) => setShiftTypes(r.data)).catch(() => {});
  }, [weekStart]);
  useEffect(() => { load(); }, [load]);

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  });
  const dayNames = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

  const shiftOf = (userId, dayIso) => data.shifts.find((s) => s.user_id === userId && s.date === dayIso);
  const leaveOf = (userId, dayIso) => data.leaves.find((l) => l.user_id === userId && l.leave_type !== "hourly" && l.start_date <= dayIso && dayIso < l.end_date);
  // Hourly leave is a same-day partial leave — the person still works their shift.
  const hourlyLeaveOf = (userId, dayIso) => data.leaves.find((l) => l.user_id === userId && l.leave_type === "hourly" && (l.start_date || "").slice(0, 10) === dayIso);
  const hourlyText = (hlv) => `SAATLİK İZİN (${hlv.start_time || "?"}-${hlv.end_time || "?"})`;
  // For a given day of a leave, decide the label. Days flagged as weekend/public
  // holiday / paid-excuse (off_dates) are NOT part of the annual leave — show the
  // exact label the requester picked instead of "Yıllık İzin".
  const leaveBadgeFor = (lv, dayIso) => {
    const isOff = (lv.off_dates || []).includes(dayIso);
    if (isOff) return { text: lv.off_dates_labels?.[dayIso] || "Hafta Tatili / Resmi Tatil", off: true };
    return { text: `İzinli · ${LEAVE_LABELS[lv.leave_type] || ""}`, off: false };
  };

  const setShift = async (userId, dayIso, value) => {
    if (!canEdit) return;
    try {
      let body = { user_id: userId, date: dayIso };
      if (value.startsWith("type:")) {
        body.shift_type_id = value.replace("type:", "");
      } else if (value.startsWith("leave:")) {
        body.leave_type = value.replace("leave:", "");
        body.shift = "off";
      } else if (value === "off") {
        body.shift = "off";
      }
      const r = await api.post("/shifts", body);
      if (r.data.conflict_with_leave) toast.warning("Bu personel o gün onaylı izinde!");
      load();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };
  const clearShift = async (userId, dayIso) => {
    if (!canEdit) return;
    await api.delete(`/shifts/${userId}/${dayIso}`);
    load();
  };

  const openNewType = () => { setEditingType(null); setTypeForm({ ...DEFAULT_TYPE_FORM, department: user?.role === "manager" ? user?.department : null }); setTypeDialogOpen(true); };
  const openEditType = (t) => { setEditingType(t); setTypeForm({ code: t.code, label: t.label, start_time: t.start_time, end_time: t.end_time, color: t.color || "#C05A42", department: t.department }); setTypeDialogOpen(true); };
  const submitType = async () => {
    if (!typeForm.code || !typeForm.label) return toast.error("Kod ve isim zorunlu");
    try {
      if (editingType) await api.patch(`/shift-types/${editingType.id}`, typeForm);
      else await api.post("/shift-types", typeForm);
      toast.success(editingType ? "Güncellendi" : "Eklendi");
      setTypeDialogOpen(false); load();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };
  const deleteType = async (t) => {
    if (!window.confirm(`"${t.code} - ${t.label}" tanımını silmek istediğinizden emin misiniz?`)) return;
    try { await api.delete(`/shift-types/${t.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const downloadXlsx = async () => {
    try {
      const r = await api.get(`/shifts/export?week_start=${isoDate(weekStart)}&weeks=${exportWeeks}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `vardiyalar_${isoDate(weekStart)}_${exportWeeks}hafta.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  // Plain-text content of a cell for the print view (no dropdowns).
  const cellText = (userId, dayIso) => {
    const s = shiftOf(userId, dayIso);
    const lv = leaveOf(userId, dayIso);
    const hlv = hourlyLeaveOf(userId, dayIso);
    let base = "-";
    if (s?.shift_type) base = `${s.shift_type.code} · ${s.shift_type.label}<br>${s.shift_type.start_time}-${s.shift_type.end_time}`;
    else if (s?.leave_type) base = `İzin · ${LEAVE_LABELS[s.leave_type] || s.leave_type}`;
    else if (lv) { const b = leaveBadgeFor(lv, dayIso); base = b.text; }
    else if (s?.shift === "off") base = "İzinli";
    // Hourly leave is shown alongside the assigned shift, e.g.
    // "A · SABAH 09:00-17:00 / SAATLİK İZİN (13:00-17:00)".
    if (hlv) {
      const ht = hourlyText(hlv);
      base = (base && base !== "-") ? `${base}<br>/ ${ht}` : ht;
    }
    return base;
  };

  // Print: dedicated window, grouped by department, ONE department per page.
  const printSchedule = () => {
    const groups = {};
    visiblePersonnel.forEach((p) => { (groups[p.department || "—"] = groups[p.department || "—"] || []).push(p); });
    const deptNames = Object.keys(groups);
    if (deptNames.length === 0) { toast.error("Yazdırılacak personel yok"); return; }
    const weekLabel = `${days[0].toLocaleDateString("tr-TR")} - ${days[6].toLocaleDateString("tr-TR")}`;
    const dayHeaders = days.map((d, i) => `${dayNames[i]} ${d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" })}`);
    let body = "";
    deptNames.forEach((dept) => {
      const rows = groups[dept].map((p) => {
        const cells = days.map((d) => `<td>${cellText(p.id, isoDate(d))}</td>`).join("");
        return `<tr><td class="name">${p.first_name} ${p.last_name}</td>${cells}</tr>`;
      }).join("");
      body += `<section class="dept"><h2>${dept} — Vardiya Planı</h2><div class="wk">Hafta: ${weekLabel}</div>`
        + `<table><thead><tr><th>Personel</th>${dayHeaders.map((h) => `<th>${h}</th>`).join("")}</tr></thead><tbody>${rows}</tbody></table></section>`;
    });
    const html = `<!DOCTYPE html><html lang="tr"><head><meta charset="utf-8"><title>Vardiya Planı</title><style>`
      + `*{font-family:Arial,Helvetica,sans-serif;box-sizing:border-box}`
      + `body{margin:0;color:#111}`
      + `.dept{padding:20px 24px;page-break-after:always}`
      + `.dept:last-child{page-break-after:auto}`
      + `h2{font-size:18px;margin:0 0 4px}`
      + `.wk{font-size:12px;color:#555;margin-bottom:10px}`
      + `table{width:100%;border-collapse:collapse;font-size:11px}`
      + `th,td{border:1px solid #999;padding:6px;text-align:left;vertical-align:top}`
      + `th{background:#eee;text-transform:uppercase;font-size:9px;letter-spacing:.04em}`
      + `td.name{font-weight:bold;white-space:nowrap}`
      + `@page{size:A4 landscape;margin:10mm}`
      + `</style></head><body>${body}</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Yazdırma penceresi açılamadı (popup engelleyiciyi kapatın)"); return; }
    w.document.open(); w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* noop */ } }, 400);
  };

  // Filter personnel by role. Admin/HR/GM can view any department (deptFilter).
  const visiblePersonnel = user?.role === "staff"
    ? personnel.filter((p) => p.id === user.id)
    : user?.role === "manager"
      ? personnel.filter((p) => p.department === user.department)
      : (deptFilter === "__all__" ? personnel : personnel.filter((p) => p.department === deptFilter));

  const departments = [...new Set(personnel.map((p) => p.department).filter(Boolean))].sort();

  const relevantTypes = shiftTypes; // backend already filters for managers

  const renderCell = (userId, dayIso) => {
    const s = shiftOf(userId, dayIso);
    const lv = leaveOf(userId, dayIso);
    const hlv = hourlyLeaveOf(userId, dayIso);
    const hourlyBadge = hlv ? (
      <Badge variant="outline" className="text-[9px] border-amber-500 text-amber-700 dark:text-amber-400 block w-fit" data-testid={`shift-hourly-badge-${userId}-${dayIso}`}>
        {hourlyText(hlv)}
      </Badge>
    ) : null;
    if (!canEdit) {
      // Read-only display
      if (s?.shift_type) {
        const t = s.shift_type;
        return <span className="inline-flex flex-col gap-0.5 rounded px-2 py-0.5 text-xs" style={{ background: (t.color || "#C05A42") + "22", color: t.color || "#7b2b1a" }}>
          <span className="font-semibold">{t.code} · {t.label}</span>
          <span className="opacity-70">{t.start_time}-{t.end_time}</span>
          {hourlyBadge}
        </span>;
      }
      if (s?.leave_type) return <Badge variant="secondary" className="text-[10px]">İzin · {LEAVE_LABELS[s.leave_type] || s.leave_type}</Badge>;
      if (hourlyBadge) return hourlyBadge;
      if (s?.shift === "off") return <Badge variant="outline" className="text-[10px]">İzinli</Badge>;
      return <span className="text-muted-foreground text-xs">-</span>;
    }
    // Editable dropdown
    const value = s?.shift_type_id ? `type:${s.shift_type_id}` : (s?.leave_type ? `leave:${s.leave_type}` : (s?.shift === "off" ? "off" : ""));
    return (
      <div className="relative">
        {lv && (() => { const b = leaveBadgeFor(lv, dayIso); return <Badge variant={b.off ? "secondary" : "destructive"} className="text-[9px] mb-1" data-testid={`shift-leave-badge-${userId}-${dayIso}`}>{b.text}</Badge>; })()}
        {hourlyBadge && <div className="mb-1">{hourlyBadge}</div>}
        <Select value={value} onValueChange={(v) => setShift(userId, dayIso, v)}>
          <SelectTrigger className="h-7 text-xs" data-testid={`shift-${userId}-${dayIso}`}><SelectValue placeholder="Ata" /></SelectTrigger>
          <SelectContent>
            {relevantTypes.length > 0 && (
              <>
                <div className="px-2 py-1 text-[10px] uppercase text-muted-foreground">Vardiyalar</div>
                {relevantTypes.map((t) => (
                  <SelectItem key={t.id} value={`type:${t.id}`}>{t.code} · {t.label} ({t.start_time}-{t.end_time})</SelectItem>
                ))}
              </>
            )}
            <div className="px-2 py-1 text-[10px] uppercase text-muted-foreground">İzinler</div>
            {Object.entries(LEAVE_LABELS).map(([k, v]) => (
              <SelectItem key={`lv-${k}`} value={`leave:${k}`}>{v}</SelectItem>
            ))}
            <SelectItem value="off">İzinli / Boş</SelectItem>
          </SelectContent>
        </Select>
        {s && (
          <button onClick={() => clearShift(userId, dayIso)} className="absolute -top-1 -right-1 bg-background border rounded-full p-0.5 text-muted-foreground hover:text-destructive" data-testid={`clear-${userId}-${dayIso}`}>
            <X size={10} />
          </button>
        )}
        {s?.shift_type && (
          <div className="text-[9px] text-muted-foreground mt-0.5">{s.shift_type.code} {s.shift_type.start_time}-{s.shift_type.end_time}</div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Planlama</div>
          <h1 className="font-display text-3xl font-bold mt-1">Vardiya Planı</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {["admin", "hr", "gm"].includes(user?.role) && (
            <Select value={deptFilter} onValueChange={setDeptFilter}>
              <SelectTrigger className="h-9 w-44" data-testid="dept-filter"><SelectValue placeholder="Departman" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tüm Departmanlar</SelectItem>
                {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {canManageTypes && (
            <Button variant="outline" size="sm" onClick={openNewType} data-testid="new-shift-type"><Plus size={14} className="mr-1" />Vardiya Tanımı</Button>
          )}
          {canExport && (
            <>
              <Select value={String(exportWeeks)} onValueChange={(v) => setExportWeeks(Number(v))}>
                <SelectTrigger className="h-9 w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1,2,4,8,12].map((n) => <SelectItem key={n} value={String(n)}>{n} hafta</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={downloadXlsx} data-testid="export-shifts"><Download size={14} className="mr-1" />XLSX</Button>
              <Button variant="outline" size="sm" onClick={printSchedule} data-testid="print-shifts">Yazdır</Button>
            </>
          )}
          <Button variant="outline" size="icon" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }} data-testid="prev-week"><CaretLeft size={16} /></Button>
          <div className="text-sm font-medium min-w-[180px] text-center">
            {days[0].toLocaleDateString("tr-TR")} - {days[6].toLocaleDateString("tr-TR")}
          </div>
          <Button variant="outline" size="icon" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }} data-testid="next-week"><CaretRight size={16} /></Button>
          <Button variant="secondary" size="sm" onClick={() => setWeekStart(mondayOf(new Date()))} data-testid="this-week">Bugün</Button>
        </div>
      </div>

      {canManageTypes && relevantTypes.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><Gear size={12} /> Tanımlı Vardiyalar</div>
            <div className="flex flex-wrap gap-2">
              {relevantTypes.map((t) => (
                <div key={t.id} className="flex items-center gap-1 rounded-full pl-3 pr-1 py-1 border" style={{ background: (t.color || "#eee") + "22", borderColor: (t.color || "#ccc") + "66" }}>
                  <span className="text-xs font-semibold">{t.code}</span>
                  <span className="text-xs text-muted-foreground">· {t.label} · {t.start_time}-{t.end_time}{t.department ? ` · ${t.department}` : ""}</span>
                  {(user?.role !== "manager" || t.department === user?.department) && (
                    <>
                      <button onClick={() => openEditType(t)} className="p-1 hover:bg-muted rounded" data-testid={`edit-type-${t.id}`}><PencilSimple size={12} /></button>
                      <button onClick={() => deleteType(t)} className="p-1 hover:bg-muted rounded text-destructive" data-testid={`delete-type-${t.id}`}><Trash size={12} /></button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4 md:p-6">
          <div className="overflow-x-auto printable">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left p-2 min-w-[180px]">Personel</th>
                  {days.map((d, i) => (
                    <th key={d.toISOString()} className="text-left p-2 min-w-[160px]">
                      <div className="text-xs uppercase text-muted-foreground">{dayNames[i]}</div>
                      <div className="text-sm">{d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" })}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visiblePersonnel.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="p-2 font-medium">
                      <div>{p.first_name} {p.last_name}</div>
                      <div className="text-xs text-muted-foreground">{p.department}</div>
                    </td>
                    {days.map((d) => {
                      const di = isoDate(d);
                      return <td key={di} className="p-1 align-top">{renderCell(p.id, di)}</td>;
                    })}
                  </tr>
                ))}
                {visiblePersonnel.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Personel yok</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingType ? "Vardiya Tanımını Düzenle" : "Yeni Vardiya Tanımı"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kod (kısa)</Label>
                <Input value={typeForm.code} maxLength={8} onChange={(e) => setTypeForm({...typeForm, code: e.target.value.toUpperCase()})} placeholder="A" data-testid="type-code" />
              </div>
              <div>
                <Label>İsim</Label>
                <Input value={typeForm.label} onChange={(e) => setTypeForm({...typeForm, label: e.target.value})} placeholder="Sabah" data-testid="type-label" />
              </div>
              <div>
                <Label>Başlangıç</Label>
                <Input type="time" value={typeForm.start_time} onChange={(e) => setTypeForm({...typeForm, start_time: e.target.value})} data-testid="type-start" />
              </div>
              <div>
                <Label>Bitiş</Label>
                <Input type="time" value={typeForm.end_time} onChange={(e) => setTypeForm({...typeForm, end_time: e.target.value})} data-testid="type-end" />
              </div>
              <div>
                <Label>Renk</Label>
                <Input type="color" value={typeForm.color || "#C05A42"} onChange={(e) => setTypeForm({...typeForm, color: e.target.value})} className="h-10" data-testid="type-color" />
              </div>
              <div>
                <Label>Departman</Label>
                {user?.role === "manager" ? (
                  <Input value={user?.department || ""} disabled />
                ) : (
                  <Select value={typeForm.department || "__all__"} onValueChange={(v) => setTypeForm({...typeForm, department: v === "__all__" ? null : v})}>
                    <SelectTrigger data-testid="type-dept"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Tüm Departmanlar</SelectItem>
                      {[...new Set(personnel.map((p) => p.department))].filter(Boolean).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTypeDialogOpen(false)}>Vazgeç</Button>
            <Button onClick={submitType} data-testid="type-submit">{editingType ? "Güncelle" : "Ekle"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
